import axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { isPublicAuthPage } from "@/constants/authPaths";
import { createLogger } from "@/debug/DebugingLogger";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

// Extend axios config to support retry tracking on transient session errors
declare module "axios" {
  interface InternalAxiosRequestConfig {
    _retryCount?: number;
  }
}

const log = createLogger("AxiosOfflineGuard");
axios.defaults.headers.post["Content-Type"] = "application/json";

// Runtime configuration for feature flags (injected at container startup)
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      GRAPHQL_ENABLED?: string;
    };
  }
}

// Check if GraphQL is enabled via runtime config
// Falls back to checking if the placeholder was replaced with 'true'
export const isGraphQLEnabled =
  window.__RUNTIME_CONFIG__?.GRAPHQL_ENABLED === "true";

// Silently cancel requests when offline — prevents ERR_INTERNET_DISCONNECTED
// errors from flooding the network tab and triggering error handlers.
// Uses axios.Cancel so callers can detect with axios.isCancel(error).
const offlineGuard = (config: InternalAxiosRequestConfig) => {
  if (!navigator.onLine) {
    log.debug("Request suppressed (offline): %s %s", config.method, config.url);
    throw new axios.Cancel("Request suppressed: offline");
  }
  return config;
};

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

export const dataServiceApi = axios.create({
  withCredentials: true,
  baseURL: import.meta.env.VITE_APP_DATA_SERVICE,
});

export const storageServiceApi = axios.create({
  withCredentials: true,
  baseURL: import.meta.env.VITE_APP_STORAGE_SERVICE,
});

export const graphqlServiceApi = axios.create({
  withCredentials: true,
  baseURL: import.meta.env.VITE_APP_GRAPHQL_URL,
});

// Extract table_id from request config for sticky session routing.
// The X-Table-ID header enables the load balancer to consistently route
// requests for the same table to the same data-service pod.
function extractTableId(
  config: InternalAxiosRequestConfig,
): string | undefined {
  // From JSON body (most POST endpoints)
  if (typeof config.data?.table_id === "string") return config.data.table_id;

  // From URL path: /tables/{table-id}/... or /tables/{table-id}
  const pathMatch = config.url?.match(/\/tables\/(tab-[0-9a-f-]+)(\/|$)/);
  if (pathMatch) return pathMatch[1];

  // From query params (sync endpoint)
  if (typeof config.url === "string" && config.url.includes("table_id=")) {
    const params = new URLSearchParams(config.url.split("?")[1]);
    return params.get("table_id") ?? undefined;
  }

  return undefined;
}

const tableRoutingInterceptor = (config: InternalAxiosRequestConfig) => {
  const tableId = extractTableId(config);
  if (tableId) {
    config.headers["X-Table-ID"] = tableId;
  }
  return config;
};

// Apply offline guard to all instances
axiosInstance.interceptors.request.use(offlineGuard);
dataServiceApi.interceptors.request.use(offlineGuard);
storageServiceApi.interceptors.request.use(offlineGuard);
graphqlServiceApi.interceptors.request.use(offlineGuard);

// Apply table routing header for sticky sessions on data-service
dataServiceApi.interceptors.request.use(tableRoutingInterceptor);

let sessionExpiredShown = false;
let loggingOut = false;

export const setLoggingOut = (value: boolean) => {
  loggingOut = value;
};

const setupInterceptor = (showSessionExpiredCallback: () => void) => {
  const handleSuccess = (response: AxiosResponse) => {
    sessionExpiredShown = false;
    return response;
  };

  const handleError = async (error: unknown) => {
    if (axios.isAxiosError(error)) {
      // Handle transient session errors (Redis/decode failures) with auto-retry
      if (error.response?.status === 500) {
        const sessionError = error.response?.headers?.["x-session-error"];
        if (sessionError) {
          const config = error.config;
          if (!config) return Promise.reject(error);
          config._retryCount = (config._retryCount || 0) + 1;
          if (config._retryCount <= 2) {
            const delay = config._retryCount * 1000;
            await new Promise((r) => setTimeout(r, delay));
            return axios(config);
          }
          // All retries exhausted — show error via global notification
          showNotificationGlobal(
            "Something went wrong. Please try again in a moment.",
            "error",
          );
          return Promise.reject(error);
        }
      }

      if (error.response?.status === 401) {
        const isSessionExpired =
          error.response?.headers?.["x-session-expired"] === "true";
        const currentPath = window.location.pathname;

        if (
          !sessionExpiredShown &&
          !loggingOut &&
          !isPublicAuthPage(currentPath) &&
          isSessionExpired
        ) {
          sessionExpiredShown = true;
          showSessionExpiredCallback();
        }
      }
    }
    return Promise.reject(error);
  };

  const interceptor = axiosInstance.interceptors.response.use(
    handleSuccess,
    handleError,
  );

  dataServiceApi.interceptors.response.use(handleSuccess, handleError);

  storageServiceApi.interceptors.response.use(handleSuccess, handleError);

  graphqlServiceApi.interceptors.response.use(handleSuccess, handleError);

  return interceptor;
};

export { setupInterceptor };
